import type { Campaign } from '@dashboard/types/config'
import { ORIGINAL_ID, type VersionId } from '@shared/campaignTests/audienceTest'
import type { ActionKey, ElementFact, ElementId, ResolutionReport, TargetDescriptor } from '@shared/editor/model'
import { matchesUrlCriteria } from '@shared/urlCriteria'
import { audienceTerms, campaignActions, originalLayerEntries } from './campaignEdit'
import type { ActionEntry, StoredPageRule } from './campaignEdit'
import { experienceLabel } from '@dashboard/services/string'
import { audienceDisplayName, type GroupCatalogEntry } from './groups'
import type { Audience, Selection } from './projection'

const MIN_CHANGED_AUDIENCES_FOR_SPLIT = 5

export type AudienceRow = {
  id: string
  name: string
  operator: 'all' | 'any'
  terms: { groupId: string; groupName: string; segmentId: string; segmentName: string }[]
  sentence: string
  counts: { onPage: number | null; otherChanges: number | null; total: number }
  hasOnPageChanges: boolean
}
export type AudienceSection = {
  key: string
  name: string
  kind: 'question' | 'segment-group' | 'multiple'
  kindLabel: string
  rows: AudienceRow[]
}
export type SelectionView = {
  element?: ElementId
  target: TargetDescriptor
  label: string
  actionKey?: ActionKey
  actions: ActionKey[]
}
export type ChangeCard = {
  key: string
  letter?: string
  label: string
  text: string
  pageLabel?: string
  url?: string | null
  pattern?: boolean
  actionKeys: ActionKey[]
  element?: ElementId
  missing: boolean
  matchCount: number
}
export type GhostCard = {
  key: string
  label: string
  audiences: string[]
  originalText: string
  element: ElementId
}
export type ChangesView = { onPage: ChangeCard[]; otherChanges: ChangeCard[]; otherAudiences: GhostCard[] }
export type EditorView = {
  sections: AudienceSection[]
  onPageSections: AudienceSection[]
  otherPageSections: AudienceSection[]
  order: Audience[]
  activeRow: AudienceRow | null
  selection: SelectionView | null
  changes: ChangesView
}
export type EditorViewInput = {
  groups: GroupCatalogEntry[]
  audience: Audience
  pageUrl: string
  report: ResolutionReport | null
  selection: Selection | null
  otherAudiencesExpanded?: boolean
  /** The original page's selected version, used while the original page is the active node. */
  originalVersionId?: VersionId
}

/** Every change the view lists: the active original page's layer first, then each audience's changes. */
function viewActions(campaign: Campaign, input: Pick<EditorViewInput, 'audience' | 'originalVersionId'>) {
  return [
    ...(input.audience.kind === 'original' ? originalLayerEntries(campaign, input.originalVersionId) : []),
    ...campaignActions(campaign)
  ]
}

function activeNodeId(audience: Audience): string {
  return audience.kind === 'variant' ? audience.id : ORIGINAL_ID
}

export function reorderAudienceSection(
  campaign: Campaign,
  sections: readonly AudienceSection[],
  sectionKey: string,
  beforeKey: string | null
): string[] {
  const moved = new Set(sections.find((section) => section.key === sectionKey)?.rows.map((row) => row.id))
  const before = new Set(sections.find((section) => section.key === beforeKey)?.rows.map((row) => row.id))
  const highestFirst = campaign.variants.map((audience) => audience.id).reverse()
  const block = highestFirst.filter((id) => moved.has(id))
  const remaining = highestFirst.filter((id) => !moved.has(id))
  const index = remaining.findIndex((id) => before.has(id))
  remaining.splice(index < 0 ? remaining.length : index, 0, ...block)
  return remaining
}

function letterAt(index: number): string {
  let letter = ''
  for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26)) {
    letter = String.fromCharCode(65 + ((value - 1) % 26)) + letter
  }
  return letter
}

function exactPageUrl(rule: StoredPageRule, pageUrl: string): string | null {
  if (
    (rule.operator && rule.operator !== 'equals') ||
    rule.path.includes('*') ||
    (rule.domain !== '*' && rule.domain.includes('*'))
  ) {
    return null
  }
  try {
    const current = new URL(pageUrl)
    const origin = rule.domain === '*' || current.hostname === rule.domain ? current.origin : `https://${rule.domain}`
    const url = new URL(`/${rule.path.replace(/^\/+/, '')}`, origin).href
    return matchesUrlCriteria(rule, url) ? url : null
  } catch {
    return null
  }
}

function cardText(entries: ActionEntry[]): string {
  let text = ''
  for (const { action } of entries) {
    const modifications = action.modifications
    if (modifications?.text !== undefined) {
      text = experienceLabel(modifications.text)
    } else {
      for (const pair of modifications?.findReplace ?? []) {
        if (pair.find) {
          text = text.replaceAll(pair.find, experienceLabel(pair.replace))
        }
      }
    }
  }
  return text
}

function reportedText(element: ElementFact | undefined): string | undefined {
  if (!element) {
    return undefined
  }
  switch (element.tag.toLowerCase()) {
    case 'img':
    case 'input':
    case 'textarea':
    case 'select':
      return element.text
    default:
      return element.fullText ?? element.text
  }
}

function classifyChanges(
  campaign: Campaign,
  input: Pick<EditorViewInput, 'audience' | 'originalVersionId' | 'pageUrl' | 'report'>
) {
  const hasPageUrl = URL.canParse(input.pageUrl)
  const resolutions = new Map(input.report?.inventory.map((result) => [result.key, result]))
  return viewActions(campaign, input).map((entry) => {
    const resolution = resolutions.get(entry.key)
    const location =
      hasPageUrl && !entry.action.page.some((rule) => matchesUrlCriteria(rule, input.pageUrl))
        ? 'other-page'
        : !input.report
          ? 'pending'
          : resolution?.status === 'resolved' && resolution.elements.length
            ? 'on-page'
            : 'missing'
    const target = JSON.stringify([entry.action.selector, entry.action.targeting])
    const cardKey =
      location === 'other-page'
        ? `page:${JSON.stringify([
            target,
            entry.action.page.map((rule) => JSON.stringify([rule.domain, rule.path, rule.operator])).sort()
          ])}`
        : `missing:${target}`
    return { entry, resolution, location, cardKey }
  })
}

export function deriveChanges(
  campaign: Campaign,
  input: Pick<EditorViewInput, 'audience' | 'originalVersionId' | 'pageUrl' | 'report'>,
  rows: AudienceRow[] = []
): ChangesView {
  const actions = classifyChanges(campaign, input)
  const activeId = activeNodeId(input.audience)
  const resolutions = new Map(input.report?.inventory.map((result) => [result.key, result]))
  const elements = new Map(input.report?.elements.map((element) => [element.id, element]))
  const onElements = new Map<ElementId, ActionEntry[]>()
  const missing = new Map<string, ActionEntry[]>()
  const offPage = new Map<string, ActionEntry[]>()
  const others = new Map<ElementId, Map<string, string>>()
  const append = <Key>(map: Map<Key, ActionEntry[]>, key: Key, entry: ActionEntry) => {
    const entries = map.get(key)
    if (entries) {
      entries.push(entry)
    } else {
      map.set(key, [entry])
    }
  }
  for (const { entry, resolution, location, cardKey } of actions) {
    if (entry.audience.id === activeId) {
      if (location === 'other-page') {
        append(offPage, cardKey, entry)
      } else {
        if (location === 'on-page' && resolution) {
          for (const element of resolution.elements) {
            append(onElements, element, entry)
          }
        } else {
          append(missing, cardKey, entry)
        }
      }
    } else if (location === 'on-page') {
      for (const element of resolution?.elements ?? []) {
        const names = others.get(element) ?? new Map<string, string>()
        names.set(
          entry.audience.id,
          rows.find((row) => row.id === entry.audience.id)?.name || audienceDisplayName(entry.audience, [])
        )
        others.set(element, names)
      }
    }
  }
  const card = (key: string, entries: ActionEntry[], element?: ElementId): ChangeCard => ({
    key,
    label: (element ? elements.get(element)?.label : undefined) ?? entries[0]?.action.selector ?? 'Element',
    text: reportedText(element ? elements.get(element) : undefined) ?? cardText(entries),
    actionKeys: entries.map((entry) => entry.key),
    ...(element ? { element } : {}),
    missing: !element,
    matchCount: Math.max(1, ...entries.map((entry) => resolutions.get(entry.key)?.elements.length ?? 0))
  })
  const onPage = [...onElements]
    .sort(([left], [right]) => (elements.get(left)?.order ?? 0) - (elements.get(right)?.order ?? 0))
    .map(([element, entries], index) => ({ ...card(`element:${element}`, entries, element), letter: letterAt(index) }))
  const otherChanges: ChangeCard[] = []
  for (const [key, entries] of offPage) {
    const first = entries[0]
    if (!first) {
      continue
    }
    const url = first.action.page.map((rule) => exactPageUrl(rule, input.pageUrl)).find((value) => value !== null)
    otherChanges.push({
      ...card(key, entries),
      missing: false,
      pageLabel: first.action.page.map((rule) => rule.path).join(', '),
      url: url ?? null,
      pattern: !url
    })
  }
  const unresolved = [...missing].map(([key, entries]) => card(key, entries))
  return {
    onPage: input.report ? onPage : [...onPage, ...unresolved],
    otherChanges: input.report ? [...unresolved, ...otherChanges] : otherChanges,
    otherAudiences: [...others]
      .filter(([element]) => !onElements.has(element))
      .sort(([left], [right]) => (elements.get(left)?.order ?? 0) - (elements.get(right)?.order ?? 0))
      .map(([element, audiences]) => ({
        key: `element:${element}`,
        element,
        label: elements.get(element)?.label ?? 'Element',
        originalText: reportedText(elements.get(element)) ?? '',
        audiences: [...audiences.values()]
      }))
  }
}

export function sameTarget(left: TargetDescriptor, right: TargetDescriptor): boolean {
  return left.selector === right.selector && JSON.stringify(left.targeting) === JSON.stringify(right.targeting)
}

export function deriveEditorView(campaign: Campaign, input: EditorViewInput): EditorView {
  const actions = viewActions(campaign, input)
  const hasPageUrl = URL.canParse(input.pageUrl)
  const classified = classifyChanges(campaign, input)
  const rows: AudienceRow[] = campaign.variants.map((audience) => {
    const targeting = audienceTerms(audience)
    const terms = targeting.terms.map((term) => {
      const group = input.groups.find((item) => item.id === term.groupId)
      return {
        groupId: term.groupId,
        groupName: group?.name ?? 'Deleted group',
        segmentId: term.segmentId,
        segmentName: group?.segments.find((segment) => segment.id === term.segmentId)?.name ?? 'Deleted segment'
      }
    })
    const joiner = targeting.operator === 'any' ? ' or ' : ' and '
    const grouped = new Map<string, { name: string; segments: string[] }>()
    for (const term of terms) {
      const group = grouped.get(term.groupId)
      if (group) {
        group.segments.push(term.segmentName)
      } else {
        grouped.set(term.groupId, { name: term.groupName, segments: [term.segmentName] })
      }
    }
    const sentence = [...grouped.values()]
      .map((group) => `${group.name} is ${group.segments.join(joiner)}`)
      .join(joiner)
    const entries = classified.filter(({ entry }) => entry.audience.id === audience.id)
    const onPage = new Set<ElementId>()
    const otherChanges = new Set<string>()
    for (const { resolution, location, cardKey } of entries) {
      if (location === 'on-page' && resolution) {
        for (const element of resolution.elements) {
          onPage.add(element)
        }
      } else {
        otherChanges.add(cardKey)
      }
    }
    const hasOnPageChanges = entries.some(({ location }) => location === 'on-page' || location === 'pending')
    return {
      id: audience.id,
      name: audienceDisplayName(audience, input.groups),
      operator: targeting.operator,
      terms,
      sentence,
      counts: {
        onPage: input.report ? onPage.size : null,
        otherChanges: input.report ? otherChanges.size : null,
        total: entries.length
      },
      hasOnPageChanges
    }
  })
  const sections: AudienceSection[] = []
  const assigned = new Set<string>()
  for (const group of input.groups) {
    const grouped = rows.filter((row) => row.terms.length > 0 && row.terms.every((term) => term.groupId === group.id))
    if (grouped.length) {
      grouped.forEach((row) => assigned.add(row.id))
      sections.push({
        key: group.id,
        name: group.name,
        kind: group.kind,
        kindLabel: `${group.kind === 'question' ? 'Question' : 'Segment group'} · ${grouped.length}`,
        rows: grouped
      })
    }
  }
  const multiple = rows.filter((row) => !assigned.has(row.id))
  if (multiple.length) {
    sections.push({
      key: 'multiple',
      name: 'Multiple groups',
      kind: 'multiple',
      kindLabel: `Multiple groups · ${multiple.length}`,
      rows: multiple
    })
  }
  const positions = new Map(rows.map((row, index) => [row.id, index]))
  const sectionRank = (section: AudienceSection) => Math.max(...section.rows.map((row) => positions.get(row.id) ?? -1))
  sections.sort((left, right) => sectionRank(right) - sectionRank(left))
  const onPageSections: AudienceSection[] = []
  const otherPageSections: AudienceSection[] = []
  const splitAudiences =
    !!input.report && rows.filter((row) => row.hasOnPageChanges).length >= MIN_CHANGED_AUDIENCES_FOR_SPLIT
  for (const section of sections) {
    const onPage: AudienceRow[] = []
    const otherPages: AudienceRow[] = []
    for (const row of section.rows) {
      if (!splitAudiences || row.hasOnPageChanges) {
        onPage.push(row)
      } else {
        otherPages.push(row)
      }
    }
    const kind =
      section.kind === 'question' ? 'Question' : section.kind === 'multiple' ? 'Multiple groups' : 'Segment group'
    if (onPage.length) {
      onPageSections.push({ ...section, rows: onPage, kindLabel: `${kind} · ${onPage.length}` })
    }
    if (otherPages.length) {
      otherPageSections.push({ ...section, rows: otherPages, kindLabel: `${kind} · ${otherPages.length}` })
    }
  }
  const visibleSections = input.otherAudiencesExpanded ? [...onPageSections, ...otherPageSections] : onPageSections
  const selected = input.selection
  const selectedElement = selected?.element
  const activeId = activeNodeId(input.audience)
  const selectedActions = selected
    ? actions
        .filter(
          (entry) =>
            entry.audience.id === activeId &&
            (entry.key === selected.actionKey ||
              (selectedElement &&
                input.report?.inventory.some(
                  (result) => result.key === entry.key && result.elements.includes(selectedElement)
                )) ||
              ((!selectedElement || !input.report?.inventory.some((result) => result.key === entry.key)) &&
                (!hasPageUrl || entry.action.page.some((rule) => matchesUrlCriteria(rule, input.pageUrl))) &&
                sameTarget(
                  { selector: entry.action.selector ?? '', targeting: entry.action.targeting },
                  selected.target
                )))
        )
        .map((entry) => entry.key)
    : []
  return {
    sections,
    onPageSections,
    otherPageSections,
    order: [
      { kind: 'original' },
      ...visibleSections.flatMap((section) => section.rows.map((row): Audience => ({ kind: 'variant', id: row.id })))
    ],
    activeRow: rows.find((row) => row.id === activeId) ?? null,
    changes: deriveChanges(campaign, input, rows),
    selection: selected
      ? {
          ...selected,
          label:
            input.report?.elements.find((element) => element.id === selected.element)?.label ??
            selected.target.selector,
          actions: selectedActions
        }
      : null
  }
}
